import React, { useState, useRef } from 'react';
import { apiUrl } from '../config/api';
import { useToast } from './Toast';
import { getCustomThresholds } from './ThresholdEditor';
import './BatchUpload.css';

interface BatchFile {
  file: File;
  name: string;
  size: number;
  status: 'pending' | 'uploading' | 'complete' | 'error';
  scanId?: string;
  error?: string;
  thumbnail?: string;
}

export const BatchUpload: React.FC = () => {
  const [files, setFiles] = useState<BatchFile[]>([]);
  const [expanded, setExpanded] = useState(false);
  const { toast } = useToast();
  const [profile, setProfile] = useState('google-like');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processFiles = async (selectedFiles: File[]) => {
    const batchFiles: BatchFile[] = [];
    
    for (const f of selectedFiles) {
      const batchFile: BatchFile = {
        file: f,
        name: f.name,
        size: f.size,
        status: 'pending' as const
      };
      batchFiles.push(batchFile);
    }
    
    setFiles(batchFiles);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    processFiles(selectedFiles);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if leaving the drop-zone itself
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x <= rect.left || x >= rect.right || y <= rect.top || y >= rect.bottom) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files).filter(f => 
      f.name.endsWith('.zip')
    );
    
    if (droppedFiles.length > 0) {
      processFiles(droppedFiles);
      setExpanded(true);
    }
  };

  const uploadBatch = async () => {
    for (let i = 0; i < files.length; i++) {
      setFiles(prev => {
        const updated = [...prev];
        updated[i].status = 'uploading';
        return updated;
      });

      try {
        const formData = new FormData();
        formData.append('file', files[i].file);

        const customThresholds = getCustomThresholds();
        let url = apiUrl(`/upload?profile=${profile}`);
        
        if (customThresholds) {
          url += `&zipBytesMax=${customThresholds.zipBytesMax * 1024}`;
          url += `&filesMax=${customThresholds.filesMax}`;
          url += `&initialKbMax=${customThresholds.initialKbMax}`;
          url += `&subloadKbMax=${customThresholds.subloadKbMax}`;
        }

        const response = await fetch(url, {
          method: 'POST',
          body: formData,
          credentials: 'include'
        });

        const data = await response.json();
        
        if (data.success) {
          const scanId = data.shareUrl?.split('/').pop();
          const passed = data.data?.summary?.pass || false;
          const thumbnail = data.data?.runtime?.screenshot?.base64;
          
          setFiles(prev => {
            const updated = [...prev];
            updated[i].status = passed ? 'complete' : 'error';
            updated[i].scanId = scanId;
            updated[i].thumbnail = thumbnail;
            updated[i].error = passed ? undefined : `Failed: ${data.data?.summary?.failCount || 0} checks`;
            return updated;
          });
        } else {
          throw new Error(data.message || data.error || 'Upload failed');
        }
      } catch (error) {
        setFiles(prev => {
          const updated = [...prev];
          updated[i].status = 'error';
          updated[i].error = error instanceof Error ? error.message : 'Upload failed';
          return updated;
        });
      }
    }
    // Show completion toast
    toast('Batch processing complete! All files have been validated.', 'success');
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'uploading': return '⬆️';
      case 'complete': return '✅';
      case 'error': return '❌';
      default: return '•';
    }
  };

  if (!expanded) {
    return (
      <div className="batch-upload-compact">
        <button onClick={() => setExpanded(true)} className="expand-btn">
          📦 Batch Upload Creatives
        </button>
      </div>
    );
  }

  return (
    <div className="batch-upload">
      <div className="batch-header">
        <h3>📦 Batch Upload & Validation</h3>
        <button onClick={() => setExpanded(false)} className="close-btn">✕</button>
      </div>

      <div className="batch-controls">
        <div className="profile-selector">
          <label>Validation Profile:</label>
          <select value={profile} onChange={(e) => setProfile(e.target.value)}>
            <option value="google-like">Google-like</option>
            <option value="iab-baseline">IAB Baseline</option>
          </select>
        </div>

        <div 
          className={`drop-zone ${isDragging ? 'dragging' : ''}`}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => {
            // Prevent click during drag operations
            if (!isDragging) {
              fileInputRef.current?.click();
            }
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".zip"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <div className="drop-zone-content">
            <span className="drop-icon">📁</span>
            <p><strong>Drop ZIP files here</strong> or click to browse</p>
            <p className="drop-hint">Select multiple files to validate in batch</p>
          </div>
        </div>

        {files.length > 0 && (
          <button onClick={uploadBatch} className="upload-btn">
            Upload & Validate ({files.length} files)
          </button>
        )}
      </div>

      {files.length > 0 && (
        <div className="batch-list">
          <table>
            <thead>
              <tr>
                <th>Preview</th>
                <th>Status</th>
                <th>File Name</th>
                <th>Size</th>
                <th>Scan ID</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {files.map((file, idx) => (
                <tr key={idx}>
                  <td>
                    {file.thumbnail ? (
                      <img 
                        src={`data:image/jpeg;base64,${file.thumbnail}`}
                        alt="Preview"
                        style={{
                          width: '60px',
                          height: '45px',
                          objectFit: 'cover',
                          border: '1px solid var(--border-color)',
                          borderRadius: '4px'
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '60px',
                        height: '45px',
                        backgroundColor: 'var(--bg-tertiary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px'
                      }}>
                        📦
                      </div>
                    )}
                  </td>
                  <td>{getStatusIcon(file.status)}</td>
                  <td>
                    {file.name}
                    {file.error && (
                      <div style={{ fontSize: '11px', color: '#b91c1c', marginTop: '4px' }}>
                        {file.error}
                      </div>
                    )}
                  </td>
                  <td>{(file.size / 1024).toFixed(1)} KB</td>
                  <td>
                    {file.scanId ? (
                      <a 
                        href={`/scans/${file.scanId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'var(--border-hover)', textDecoration: 'underline' }}
                      >
                        {file.scanId.substring(0, 8)}...
                      </a>
                    ) : '-'}
                  </td>
                  <td>
                    {file.status === 'pending' && (
                      <button
                        onClick={() => setFiles(prev => prev.filter((_, i) => i !== idx))}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: 'var(--text-muted)',
                          cursor: 'pointer',
                          fontSize: '16px',
                          padding: '2px 6px',
                        }}
                        title="Remove file"
                      >✕</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="batch-note">
        Upload multiple HTML5 creatives. Drag & drop supported.
      </p>
    </div>
  );
};
